// Library Methods
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

// Hooks
import useTokenAccess from "src/hooks/auth/useTokenAccess";
import { useSnackbarContext } from "src/contexts/alerts/SnackbarContext";

// API
import { favoriteExternalExam } from "../../api";

export const useUpdateFavoriteExam = (
  clinicId: string | undefined,
  patientId: string,
  externalExamId: string
) => {
  const queryClient = useQueryClient();
  const { getToken } = useTokenAccess();
  const { t } = useTranslation();
  const { setSnackbarInfo } = useSnackbarContext();

  return useMutation({
    mutationFn: async (isFavorite: boolean) => {
      if (!clinicId) throw new Error();

      try {
        const token = await getToken();
        return favoriteExternalExam(
          token,
          clinicId,
          patientId,
          externalExamId,
          isFavorite
        );
      } catch (error) {
        throw error;
      }
    },
    onSuccess: () => {
      // The single-exam detail cache (used by e.g. the comparison screen)...
      queryClient.invalidateQueries({
        queryKey: ["external-exams", externalExamId],
      });
      // ...and the list caches SelectionCard's `isFavorited` prop is
      // actually sourced from (ExternalExamsContext syncs sortableCards
      // from these), so the star icon reflects the change without a
      // manual refresh -- same set useDeleteExternalExams invalidates.
      queryClient.invalidateQueries({
        queryKey: ["vf-external-exams", clinicId, patientId],
      });
      queryClient.invalidateQueries({
        queryKey: ["opd-external-exams", clinicId, patientId],
      });
      queryClient.invalidateQueries({
        queryKey: ["oct-external-exams", clinicId, patientId],
      });
      setSnackbarInfo({
        variant: "success",
        message: t("Favorite status updated successfully!"),
      });
    },
    onError: () => {
      setSnackbarInfo({
        variant: "error",
        message: t("Favorite status could not be updated!"),
      });
    },
  });
};
